import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  Receipt,
  BookOpen,
  BarChart3,
  CalendarClock,
  SlidersHorizontal,
  MessageSquareText,
  BadgeInfo,
  UserCog,
  LogOut,
  UserRound,
  ShieldCheck,
  ShieldEllipsis,
  KeyRound,
  Plug,
  Building2,
  ChevronRight,
} from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { isWalletAdminUser, isWalletAgentUser, WALLET_ADMIN_ACTION } from "../../Utils/walletAdminAccess";
import { hasPermissionAction } from "../../Utils/organizationPermissions";

/* ── Section label map ───────────────────────── */
const SECTION_BEFORE = {
  Dashboard: "Overview",
  "Wallet Management": "Wallet",
  "User management": "Administration",
};

const W = (specific) => `${specific}||${WALLET_ADMIN_ACTION}`;

/* ── Sidebar nav (wallet product scope) ───────── */
const BASE_OPTIONS = [
  {
    heading: "Dashboard",
    title: "Dashboard",
    route: "/dashboard",
    icon: <LayoutDashboard size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_DASHBOARD_VIEW"),
  },
  {
    heading: "Wallet Management",
    title: "Wallet Management",
    icon: <Wallet size={19} strokeWidth={2} />,
    children: [
      {
        route: "/wallet/clients",
        title: "Clients",
        icon: <Building2 size={16} strokeWidth={2} />,
        PermissionKey: W("WALLET_CLIENTS_VIEW"),
      },
      {
        route: "/wallet/applications",
        title: "Agents",
        icon: <KeyRound size={16} strokeWidth={2} />,
        PermissionKey: W("WALLET_APPLICATIONS_VIEW"),
      },
    ],
  },
  {
    heading: "Transactions & ledger",
    title: "Transactions & ledger",
    icon: <Receipt size={19} strokeWidth={2} />,
    children: [
      {
        route: "/wallet/transactions",
        title: "Transactions",
        icon: <BarChart3 size={16} strokeWidth={2} />,
        PermissionKey: W("WALLET_TRANSACTIONS_VIEW"),
      },
      {
        route: "/wallet/ledger",
        title: "Ledger",
        icon: <BookOpen size={16} strokeWidth={2} />,
        PermissionKey: W("WALLET_LEDGER_VIEW"),
      },
    ],
  },
  {
    heading: "Credit management",
    title: "Credit management",
    route: "/wallet/credit",
    icon: <CalendarClock size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_CREDIT_VIEW"),
  },
  {
    heading: "Integration",
    title: "Integration",
    route: "/wallet/integration",
    icon: <Plug size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_INTEGRATION_VIEW"),
  },
  {
    heading: "Manual adjustment",
    title: "Manual adjustment",
    route: "/wallet/manual-adjustment",
    icon: <SlidersHorizontal size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_MANUAL_ADJUSTMENT"),
  },
  {
    heading: "Notifications Log",
    title: "Notifications logs",
    route: "/wallet/notifications",
    icon: <MessageSquareText size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_NOTIFICATIONS_VIEW"),
  },
  {
    heading: "White label",
    title: "White label settings",
    route: "/wallet/white-label",
    icon: <BadgeInfo size={19} strokeWidth={2} />,
    PermissionKey: W("WALLET_WHITE_LABEL_MANAGE"),
  },
  {
    heading: "User management",
    title: "User management",
    icon: <UserCog size={19} strokeWidth={2} />,
    children: [
      {
        route: "/users",
        title: "Users",
        icon: <UserRound size={16} strokeWidth={2} />,
        PermissionKey: W("USER_VIEW_ALL"),
      },
      {
        route: "/application/user-roles",
        title: "User Roles",
        icon: <ShieldCheck size={16} strokeWidth={2} />,
        PermissionKey: W("USER_ROLE_ALL"),
      },
      {
        route: "/application/user-role-permission",
        title: "Role Permissions",
        icon: <ShieldEllipsis size={16} strokeWidth={2} />,
        PermissionKey: W("USER_ROLE_PERMISSION_ALL"),
      },
    ],
  },
];

/* ── Animated collapse ───────────────────────── */
function NavChildren({ open, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(open ? "auto" : 0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      const target = el.scrollHeight;
      setH(target);
      const t = setTimeout(() => setH("auto"), 230);
      return () => clearTimeout(t);
    } else {
      setH(el.scrollHeight);
      requestAnimationFrame(() => requestAnimationFrame(() => setH(0)));
    }
  }, [open]);
  return (
    <div
      className="ems-nav-children"
      ref={ref}
      style={{ height: h === "auto" ? "auto" : h + "px" }}
    >
      <div style={{ paddingBottom: 4 }}>{children}</div>
    </div>
  );
}

/* ── SideBar ─────────────────────────────────── */
export default function SideBar({ Permission, collapsed, onRequestExpand }) {
  const { logout, userData } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebaroption, setSideBarOption] = useState(BASE_OPTIONS);
  const [openGroups, setOpenGroups] = useState(() => {
    const o = {};
    BASE_OPTIONS.forEach((item) => {
      if (item.children && item.children.some((c) => c.route === location.pathname)) {
        o[item.heading] = true;
      }
    });
    return o;
  });

  const hasPermission = (action) => {
    if (!action) return true;
    if (isWalletAdminUser(userData, Permission)) {
      return true;
    }
    return hasPermissionAction(Permission, action);
  };

  useEffect(() => {
    const filterNav = (items) =>
      items
        .map((item) => {
          if (item.children) {
            const allowedChildren = item.children.filter((child) => {
              if (
                isWalletAgentUser(userData) &&
                child.route === "/wallet/clients"
              ) {
                return false;
              }
              return child.PermissionKey ? hasPermission(child.PermissionKey) : true;
            });
            if (allowedChildren.length === 0) return null;
            return { ...item, children: allowedChildren };
          }
          if (item.PermissionKey) return hasPermission(item.PermissionKey) ? item : null;
          return item;
        })
        .filter(Boolean);

    if (!Permission || !Array.isArray(Permission)) {
      // Still filter wallet items via allowlist when permission catalog has not loaded.
      setSideBarOption(filterNav(BASE_OPTIONS));
      return;
    }
    setSideBarOption(filterNav(BASE_OPTIONS));
  }, [Permission, userData]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          logout();
          localStorage.removeItem("token");
          navigate("/login");
        } catch {
          logout();
          navigate("/login");
        }
      }
    });
  };

  const handleNav = (route) => navigate(route);

  const handleParentNavClick = (heading) => {
    if (collapsed) {
      onRequestExpand?.();
      setOpenGroups((o) => ({ ...o, [heading]: true }));
      return;
    }
    toggleGroup(heading);
  };

  const handleLeafNavClick = (route) => {
    if (collapsed) {
      onRequestExpand?.();
    }
    handleNav(route);
  };

  const toggleGroup = (heading) =>
    setOpenGroups((o) => ({ ...o, [heading]: !o[heading] }));

  let seenLabels = new Set();

  return (
    <>
      <div className="ems-sidebar-scroll">
        {sidebaroption.map((item, index) => {
          const sectionLabel = SECTION_BEFORE[item.heading];
          const showLabel = sectionLabel && !seenLabels.has(sectionLabel);
          if (showLabel) seenLabels.add(sectionLabel);

          const isActive = !item.children && item.route === location.pathname;
          const childActive = item.children && item.children.some((c) => c.route === location.pathname);
          const isOpen = !!openGroups[item.heading];

          return (
            <React.Fragment key={item.heading || index}>
              {showLabel && (
                <div className="ems-nav-section-label">{sectionLabel}</div>
              )}
              {item.children ? (
                <>
                  <button
                    className={[
                      "ems-nav-item",
                      childActive ? "parent-active" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleParentNavClick(item.heading)}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className="ems-ni-icon">{item.icon}</span>
                    <span className="ems-ni-label">{item.title}</span>
                    <span className={`ems-ni-chev${isOpen ? " open" : ""}`}>
                      <ChevronRight size={16} strokeWidth={2.4} />
                    </span>
                  </button>
                  {!collapsed && (
                    <NavChildren open={isOpen}>
                      {item.children.map((child) => (
                        <button
                          key={child.route}
                          className={[
                            "ems-nav-child",
                            location.pathname === child.route ? "active" : "",
                          ].filter(Boolean).join(" ")}
                          onClick={() => handleLeafNavClick(child.route)}
                        >
                          <span className="ems-nc-icon">{child.icon}</span>
                          <span className="ems-nc-label">{child.title}</span>
                        </button>
                      ))}
                    </NavChildren>
                  )}
                </>
              ) : (
                <button
                  className={[
                    "ems-nav-item",
                    isActive ? "active" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleLeafNavClick(item.route)}
                  title={collapsed ? item.title : undefined}
                >
                  <span className="ems-ni-icon">{item.icon}</span>
                  <span className="ems-ni-label">{item.title}</span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="ems-sidebar-foot">
        <button
          className="ems-nav-item"
          onClick={handleLogout}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="ems-ni-icon">
            <LogOut size={19} strokeWidth={2} />
          </span>
          <span className="ems-ni-label">Logout</span>
        </button>
      </div>
    </>
  );
}
